/**
 * Electron and browser download helpers.
 * Keeps Electron save dialog filters aligned with the actual media type.
 */

export const isElectron = (): boolean => {
  return !!(window as any).electronAPI || !!(window as any).require;
};

export type MediaKind = 'image' | 'video' | 'audio' | 'unknown';

/**
 * Extract a file extension from a URL or file name.
 * Supports suffixes such as `.jpg~tplv-xxx`.
 */
export const getFileExtension = (source: string): string => {
  const path = source.split('?')[0].split('#')[0];
  const fileName = path.split('/').pop() || '';
  const match = fileName.match(/\.([a-zA-Z0-9]+)(?:[~]|$)/);
  return match ? match[1].toLowerCase() : '';
};

/**
 * Infer media kind from a URL or file name extension.
 */
const getMediaKindFromSource = (source: string): MediaKind => {
  const extension = getFileExtension(source);

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension)) {
    return 'image';
  }

  if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'flv', 'wmv'].includes(extension)) {
    return 'video';
  }

  if (['mp3', 'wav', 'aac', 'm4a', 'flac', 'wma'].includes(extension)) {
    return 'audio';
  }

  return 'unknown';
};

/**
 * Build save dialog filters for the detected media type only.
 */
const getSaveDialogFilters = (mediaKind: MediaKind) => {
  switch (mediaKind) {
    case 'image':
      return [{ name: '图片', extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'] }];
    case 'video':
      return [{ name: '视频', extensions: ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'flv', 'wmv'] }];
    case 'audio':
      return [{ name: '音频', extensions: ['mp3', 'wav', 'aac', 'm4a', 'flac', 'wma'] }];
    default:
      return [{ name: '所有文件', extensions: ['*'] }];
  }
};

/**
 * Build a suggested file name from the result URL.
 */
export const getFileNameFromUrl = (url: string, fallbackName: string = 'download'): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const fileName = pathname.split('/').pop();

    if (fileName && fileName.includes('.')) {
      return fileName;
    }

    const ext = getFileExtension(url);
    return ext ? `${fallbackName}.${ext}` : fallbackName;
  } catch {
    const ext = getFileExtension(url);
    return ext ? `${fallbackName}.${ext}` : fallbackName;
  }
};

/**
 * Download a single file through Electron with a typed save dialog.
 */
export const downloadFileElectron = async (url: string, suggestedName: string): Promise<boolean> => {
  try {
    if (!isElectron()) {
      throw new Error('Not in Electron environment');
    }

    const { ipcRenderer } = (window as any).require('electron');
    const mediaKind = getMediaKindFromSource(url) !== 'unknown'
      ? getMediaKindFromSource(url)
      : getMediaKindFromSource(suggestedName);

    const result = await ipcRenderer.invoke('show-save-dialog', {
      title: '保存文件',
      defaultPath: suggestedName,
      filters: getSaveDialogFilters(mediaKind)
    });

    if (result.canceled || !result.filePath) {
      return false;
    }

    const downloadResult = await ipcRenderer.invoke('download-file', {
      url,
      filePath: result.filePath
    });

    return !!downloadResult.success;
  } catch (error) {
    console.error('Electron download failed:', error);
    return false;
  }
};

/**
 * Download multiple files through Electron into a chosen directory.
 */
export const downloadMultipleFilesElectron = async (
  urls: string[],
  taskName: string = 'download'
): Promise<{ success: number; total: number; errors: string[] }> => {
  try {
    if (!isElectron()) {
      throw new Error('Not in Electron environment');
    }

    const { ipcRenderer } = (window as any).require('electron');

    const result = await ipcRenderer.invoke('show-open-dialog', {
      title: `选择保存文件夹 - ${urls.length} 个文件`,
      properties: ['openDirectory', 'createDirectory'],
      message: `选择保存 ${urls.length} 个文件的文件夹`,
      buttonLabel: '选择文件夹'
    });

    if (result.canceled || !result.filePaths || result.filePaths.length === 0) {
      return { success: 0, total: urls.length, errors: ['用户取消了操作'] };
    }

    const saveDirectory = result.filePaths[0];
    const results = { success: 0, total: urls.length, errors: [] as string[] };

    for (let i = 0; i < urls.length; i += 1) {
      const url = urls[i];
      const fileName = getFileNameFromUrl(url, `${taskName}_${i + 1}`);
      const { ipcRenderer: pathIpc } = (window as any).require('electron');
      const filePath = await pathIpc.invoke('join-path', saveDirectory, fileName);

      try {
        const downloadResult = await ipcRenderer.invoke('download-file', {
          url,
          filePath
        });

        if (downloadResult.success) {
          results.success += 1;
        } else {
          results.errors.push(`文件 ${i + 1} (${fileName}): ${downloadResult.error || '下载失败'}`);
        }
      } catch (error) {
        results.errors.push(`文件 ${i + 1} (${fileName}): ${error}`);
      }
    }

    return results;
  } catch (error) {
    console.error('Batch Electron download failed:', error);
    return {
      success: 0,
      total: urls.length,
      errors: [`批量下载失败: ${error}`]
    };
  }
};

/**
 * Download a single file in the browser.
 */
export const downloadFileBrowser = async (url: string, fileName: string): Promise<boolean> => {
  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    return true;
  } catch (error) {
    console.error('Browser download failed:', error);

    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_blank';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    } catch {
      return false;
    }
  }
};

/**
 * Download multiple files in the browser.
 */
export const downloadMultipleFilesBrowser = async (
  urls: string[],
  taskName: string = 'download'
): Promise<{ success: number; total: number; errors: string[] }> => {
  const results = { success: 0, total: urls.length, errors: [] as string[] };

  for (let i = 0; i < urls.length; i += 1) {
    const url = urls[i];
    const fileName = getFileNameFromUrl(url, `${taskName}_${i + 1}`);

    try {
      const success = await downloadFileBrowser(url, fileName);
      if (success) {
        results.success += 1;
      } else {
        results.errors.push(`文件 ${i + 1} (${fileName}): 下载失败`);
      }

      if (i < urls.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    } catch (error) {
      results.errors.push(`文件 ${i + 1} (${fileName}): ${error}`);
    }
  }

  return results;
};

/**
 * Unified single-file download entry.
 */
export const downloadFile = async (url: string, suggestedName: string): Promise<boolean> => {
  if (isElectron()) {
    return downloadFileElectron(url, suggestedName);
  }

  return downloadFileBrowser(url, suggestedName);
};

/**
 * Unified multi-file download entry.
 */
export const downloadMultipleFiles = async (
  urls: string[],
  taskName: string = 'download'
): Promise<{ success: number; total: number; errors: string[] }> => {
  if (isElectron()) {
    return downloadMultipleFilesElectron(urls, taskName);
  }

  return downloadMultipleFilesBrowser(urls, taskName);
};
